import { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Checkbox, ModalV2, Search } from '@utilities';

import { VALUE_PICKER_CAP } from '../../utilities/constants';

import styles from './_index.module.scss';

const ValuePicker = ({ onCancel, onConfirm, source }) => {
  const [searchText, setSearchText] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [withAllOthers, setWithAllOthers] = useState(false);

  const normalizedSearch = searchText.trim().toLowerCase();
  const visibleValues = normalizedSearch
    ? source.values.filter(({ label }) => label.toLowerCase().includes(normalizedSearch))
    : source.values;
  const isAtCap = selectedIds.length >= VALUE_PICKER_CAP;

  const handleToggle = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selected) => selected !== id) : [...prev, id]
    );
  };

  const handleConfirm = () => {
    const selectedValues = source.values.filter(({ id }) => selectedIds.includes(id));
    onConfirm({ selectedValues, withAllOthers });
  };

  return (
    <ModalV2
      buttons={[
        <Button key="cancel" onClick={onCancel} text="Cancel" variant="secondary" />,
        <Button
          isDisabled={!selectedIds.length}
          key="confirm"
          onClick={handleConfirm}
          text="Add to banner"
        />,
      ]}
      isActive
      onClose={onCancel}
      title={`Select values - ${source.label}`}
    >
      <div className={styles['value-picker']}>
        <p className={styles['value-picker-hint']}>
          Choose up to {VALUE_PICKER_CAP} values. Each value becomes a banner column.
        </p>
        <Search
          hasAutoFocus
          onChange={(value) => setSearchText(String(value))}
          placeholder="Search values"
          value={searchText}
        />
        <ul className={styles['value-picker-list']}>
          {visibleValues.map(({ id, label }) => {
            const isChecked = selectedIds.includes(id);
            return (
              <li key={id}>
                <Checkbox
                  id={`value-picker-${source.id}-${id}`}
                  isChecked={isChecked}
                  isDisabled={!isChecked && isAtCap}
                  label={label}
                  onChange={() => handleToggle(id)}
                  title={label}
                  value={id}
                />
              </li>
            );
          })}
          {!visibleValues.length && (
            <li className={styles['value-picker-empty']}>No values match your search.</li>
          )}
        </ul>
        {isAtCap && (
          <p className={styles['value-picker-cap']}>
            Selection limit reached ({VALUE_PICKER_CAP}). Remove a value to pick another.
          </p>
        )}
        <Checkbox
          id={`value-picker-${source.id}-all-others`}
          isChecked={withAllOthers}
          label={'Add an "All Others" column for everyone else'}
          onChange={() => setWithAllOthers((prev) => !prev)}
          value="all-others"
        />
      </div>
    </ModalV2>
  );
};

ValuePicker.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  source: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    values: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
};

export default ValuePicker;
